import { makeStyles } from '@material-ui/core/styles'
import { Autocomplete, Box, Button, TextField } from '@mui/material'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CustomDatePicker from 'src/components/BuyPolicy/common/CustomDatePicker'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  getNextDate,
  MOTOR_MEMBER_DETAILS_VALIDATIONS,
  POLICY_TYPE_ENUM,
} from 'src/utils/constants/BuyPolicyMotor'
import { updateMotorDetails } from 'src/actions'
import { FORMIK_KEYS } from 'src/utils/constants/Bike'

const OwnDamageDetailsPopover = ({ closePopover, container }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [thirdPartyProviderName, setThirtPartyProviderName] = useState('')
  const { motorDetails, insuranceProvider } = useSelector((state) => {
    return {
      motorDetails: state?.buyPolicyMotorReducer,
      insuranceProvider: state?.insuranceProvider,
    }
  })
  const previousPolicyDetails = motorDetails?.previousPolicyDetails
  const thirdPartyDetails = {
    thirdPartyExpiryDate: previousPolicyDetails?.thirdPartyExpiryDate || '',
    thirdPartyProvider: previousPolicyDetails?.thirdPartyProvider || '',
    thirdPartyPolicyNo: previousPolicyDetails?.thirdPartyPolicyNo || '',
    thirdPartyStartDate: previousPolicyDetails?.thirdPartyStartDate || '',
    thirdPartyProviderFirstName:
      previousPolicyDetails?.thirdPartyProviderFirstName || '',
  }
  const ownDamageForm = useFormik({
    initialValues: thirdPartyDetails,
    validationSchema: Yup.object({
      thirdPartyExpiryDate: Yup.date().when(
        [FORMIK_KEYS?.EXPIRYDATE, FORMIK_KEYS?.POLICYTYPE],
        {
          is: (expiryDate, policyType) =>
            policyType === POLICY_TYPE_ENUM?.OWN_DAMAGE && expiryDate,
          then: (schema) =>
            schema
              .min(
                getNextDate(ownDamageForm?.values?.expiryDate),
                FORMIK_KEYS?.EXPIRY_DATE_SHOULD_BE_GREATER
              )
              .required(MOTOR_MEMBER_DETAILS_VALIDATIONS?.thirdPartyExpiryDate),
          otherwise: (schema) => schema,
        }
      ),

      thirdPartyProvider: Yup.string().when(
        FORMIK_KEYS?.POLICYTYPE,
        (policyType, schema) => {
          if (policyType === POLICY_TYPE_ENUM?.OWN_DAMAGE) {
            return schema.required(
              MOTOR_MEMBER_DETAILS_VALIDATIONS?.thirdPartyInsuranceProvider
            )
          }
          return schema
        }
      ),
      thirdPartyPolicyNo: Yup.string().when(
        FORMIK_KEYS?.POLICYTYPE,
        (policyType, schema) => {
          if (policyType === POLICY_TYPE_ENUM?.OWN_DAMAGE) {
            return schema.required(
              MOTOR_MEMBER_DETAILS_VALIDATIONS?.thirdPartyPolicyNumber
            )
          }
          return schema
        }
      ),
      thirdPartyStartDate: Yup.string().when(
        FORMIK_KEYS?.POLICYTYPE,
        (policyType, schema) => {
          if (policyType === POLICY_TYPE_ENUM?.OWN_DAMAGE) {
            return schema.required(
              MOTOR_MEMBER_DETAILS_VALIDATIONS?.thirdPartyStartDate
            )
          }
          return schema
        }
      ),
      thirdPartyProviderFirstName: Yup.string(),
    }),
    onSubmit: (values, { setSubmitting }) => {
      handleSubmit(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handleSubmit = async (values, setSubmitting) => {
    setSubmitting(true)
    dispatch(
      updateMotorDetails({
        vehicleDetails: {
          ...motorDetails?.vehicleDetails,
        },
        personalDetails: {
          ...motorDetails?.personalDetails,
        },
        previousPolicyDetails: {
          ...motorDetails?.previousPolicyDetails,
          thirdPartyExpiryDate: values?.thirdPartyExpiryDate,
          thirdPartyProvider: values?.thirdPartyProvider,
          thirdPartyPolicyNo: values?.thirdPartyPolicyNo,
          thirdPartyStartDate: values?.thirdPartyStartDate,
          thirdPartyProviderFirstName: values?.thirdPartyProviderFirstName,
        },
        currentPolicyDetails: {
          ...motorDetails?.currentPolicyDetails,
          policyType: container,
        },
      })
    )

    closePopover()
  }
  const handleThirdPartyChange = (event, value) => {
    ownDamageForm.setFieldValue(
      'thirdPartyProvider',
      value?.insuranceCompanyName,
      true
    )
    ownDamageForm.setFieldValue(
      'thirdPartyProviderFirstName',
      value?.insuranceCompanyFirstName,
      true
    )
  }

  return (
    <>
      <div className={classes.overAllContainer}>
        <div className={classes.expiredContainer}>
          <div className={classes.expiredContainer}>
            <div className={classes.expiredAction}>
              <span className={classes.expiredContent}>
                What is your third party start date?
              </span>
            </div>
            <div className={classes.inputWidth}>
              <CustomDatePicker
                id='thirdPartyStartDate'
                name='thirdPartyStartDate'
                placeholder='Third Party Policy Start Date *'
                value={ownDamageForm?.values?.thirdPartyStartDate}
                onChange={ownDamageForm.handleChange}
                className={classes.datePickerField}
              />
            </div>
          </div>
          <div className={classes.expiredContainer}>
            <div className={classes.expiredAction}>
              <span className={classes.expiredContent}>
                When does your third party expire?
              </span>
            </div>
            <div className={classes.inputWidth}>
              <CustomDatePicker
                id='thirdPartyExpiryDate'
                name='thirdPartyExpiryDate'
                placeholder='Third Party Policy Expiry Date *'
                value={ownDamageForm.values.thirdPartyExpiryDate}
                onChange={ownDamageForm.handleChange}
                className={classes.datePickerField}
              />
            </div>
          </div>
          <div className={classes.expiredContainer}>
            <div className={classes.expiredAction}>
              <span className={classes.expiredContent}>
                What is your third party insurer name?
              </span>
            </div>
            <Box>
              <Autocomplete
                id='thirdPartyProvider'
                name='thirdPartyPolicyProvider'
                disableClearable
                options={
                  insuranceProvider?.insuranceProviderList
                    ? insuranceProvider?.insuranceProviderList
                    : []
                }
                getOptionLabel={(option) =>
                  option?.insuranceCompanyName ||
                  ownDamageForm?.values?.thirdPartyProvider ||
                  ''
                }
                value={ownDamageForm?.values?.thirdPartyProvider || ''}
                inputValue={thirdPartyProviderName}
                onInputChange={(event, newInputValue) => {
                  setThirtPartyProviderName(newInputValue)
                }}
                onChange={(event, newInsuranceName) => {
                  handleThirdPartyChange(event, newInsuranceName)
                }}
                className={`${classes.selectWidth} ${classes.autoCompleteField}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Select third party provider *'
                    variant='outlined'
                    inputProps={{
                      ...params.inputProps,
                      className: classes.autoStyle,
                    }}
                    helperText={
                      ownDamageForm?.touched?.thirdPartyProvider &&
                      ownDamageForm?.errors?.thirdPartyProvider
                    }
                    error={
                      ownDamageForm?.touched?.thirdPartyProvider &&
                      Boolean(ownDamageForm?.errors?.thirdPartyProvider)
                    }
                  />
                )}
              />
            </Box>
          </div>
          <div className={classes.expiredContainer}>
            <div className={classes.expiredAction}>
              <span className={classes.expiredContent}>
                What is your third party policy number?
              </span>
            </div>
            <Box>
              <TextField
                id='thirdPartyPolicyNo'
                name='thirdPartyPolicyNo'
                size='small'
                placeholder='Policy Number *'
                onBlur={ownDamageForm.handleBlur}
                onChange={ownDamageForm.handleChange}
                value={ownDamageForm?.values?.thirdPartyPolicyNo}
                helperText={
                  ownDamageForm?.touched?.thirdPartyPolicyNo &&
                  ownDamageForm?.errors?.thirdPartyPolicyNo
                }
                error={
                  ownDamageForm?.touched?.thirdPartyPolicyNo &&
                  Boolean(ownDamageForm?.errors?.thirdPartyPolicyNo)
                }
                InputProps={{
                  className: classes.inputWidth,
                }}
              />
            </Box>
          </div>
          <Button
            className={classes.submitBtn}
            id='submit'
            type='submit'
            onClick={() => {
              ownDamageForm.submitForm()
            }}
          >
            Submit
          </Button>
        </div>
      </div>
    </>
  )
}

export default OwnDamageDetailsPopover
const useStyles = makeStyles({
  expiredContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
    gap: 10,
  },
  notExpiredAction: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
  },
  notExpiredContent: {
    fontSize: '11px',
    fontWeight: '400',
    fontFamily: 'roboto',
    color: '#636363',
  },
  expiryDateContent: {
    fontSize: '12px',
    fontWeight: '400',
    fontFamily: 'roboto',
    color: '#ACACAC',
  },
  expiredContent: {
    fontSize: '12px',
    fontWeight: '400',
    fontFamily: 'roboto',
  },
  overAllContainer: {
    width: '100%',
  },
  switchRoot: {
    width: '55px',
    height: '24px',
    padding: '0px',
  },
  switchBase: {
    color: '#818181',
    padding: '1px',
    '&$checked': {
      '& + $track': {
        backgroundColor: '#23bf58',
      },
    },
  },
  datePickerField: {
    '&  .MuiOutlinedInput-input': {
      height: '0.3rem',
    },
  },
  thumb: {
    color: 'white',
    width: '25px',
    height: '20px',
    margin: '1px',
  },
  autoCompleteField: {
    '& .MuiIconButton-root': {
      padding: 'unset',
    },
    '& .MuiOutlinedInput-root': {
      display: 'flex',
      justifyContent: 'space-between',
      padding: 'unset',
    },
  },
  track: {
    borderRadius: '20px',
    backgroundColor: '#818181',
    opacity: '1 !important',
    '&:after, &:before': {
      color: 'white',
      fontSize: '11px',
      fontWeight: 500,
      position: 'absolute',
      top: '6px',
    },
    '&:after': {
      content: 'Yes',
      left: '8px',
    },
    '&:before': {
      content: 'No',
      right: '7px',
    },
  },
  checked: {
    color: '#23bf58 !important',
    transform: 'translateX(26px) !important',
  },
  autoStyle: {
    fontSize: '12px !important',
    height: '2px !important',
    color: 'black',
    padding: 'unset ',
  },
  inputWidth: {
    maxWidth: 200,
    fontSize: '12px !important',
  },
  selectWidth: {
    minWidth: 200,
    fontSize: '12px !important',
  },
  submitBtn: {
    background: 'linear-gradient(180deg, #4DC3E3 0%, #1EA0C4 100%)',
    fontSize: '12px !important',
  },
})
